// Checkout entry for the CONSUMER "AI credit assistant" funnel (NX Funnels
// /ai-credit links here): /start-checkout?offer=consumer&tier=solo|family|unlimited
//
// Separate from the business /pricing page. Same flow: the visitor's details are
// saved as a lead FIRST (offer=consumer), then Stripe Checkout; access is granted
// only by the Stripe webhook after payment. The buyer must tick the
// do-it-yourself box — the server refuses checkout without it.
//
// It is self-service software: the buyer approves and sends every dispute, we
// never dispute for them, and nothing here may promise a score or a deletion.
import { BadgeCheck, CalendarClock, CreditCard, Loader2, Lock, RotateCcw, ShieldCheck, Timer, Users, Zap } from "lucide-react";
import { SalesVideo, StickyStart } from "../components/SalesKit";
import { SALES_VIDEOS } from "../lib/sales-videos";
import { pixelEvent, pixelPageView } from "../lib/meta-pixel";
import { useEffect, useMemo, useState } from "react";
import "../styles/software-landing.css";
import { ApiError, softwareSales, type ConsumerTier, type SoftwarePlan, type SoftwareSalesConfig } from "../lib/api";
import { boundedReferrer, friendlyError, money, readUtm } from "./SoftwarePricing";

const TIERS: ConsumerTier[] = ["solo", "family", "unlimited"];
const TIER_COPY: Record<ConsumerTier, { who: string; people: string }> = {
  solo: { who: "Just yourself", people: "1 person" },
  family: { who: "You and your family", people: "Up to 5 people" },
  unlimited: { who: "No limit on people", people: "Unlimited people" },
};
// The funnel's launch special is shown against this regular monthly price.
// Display only — the amount actually charged always comes from Stripe.
const REGULAR_PRICE_CENTS = 20000;
// Done-for-you credit repair starts with a free call on Key's calendar (owned by
// the Blueprint booking site); ?topic pre-checks "Credit Repair" on the form.
const DFY_BOOKING_URL = "https://booking.noexcuseblueprint.com/book/low-key/mr-no-excuses?topic=Credit%20Repair";

function parseTier(v: string | null): ConsumerTier {
  return v === "solo" || v === "family" || v === "unlimited" ? v : "solo";
}

export default function StartCheckoutPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [cfg, setCfg] = useState<SoftwareSalesConfig<ConsumerTier> | null>(null);
  const [tier, setTier] = useState<ConsumerTier>(() => parseTier(params.get("tier")));
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", website: "" });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const utm = useMemo(readUtm, []);
  const cancelled = params.get("checkout") === "cancelled";

  useEffect(() => {
    document.title = "Start your plan — No Excuses Dispute Manager";
    pixelPageView();
    softwareSales.consumerConfig().then(setCfg).catch(() => setCfg({ ok: true, enabled: false, plans: [] }));
  }, []);

  const plans = useMemo(() => {
    const list = cfg?.plans || [];
    return TIERS.map((t) => list.find((p) => p.tier === t)).filter(Boolean) as SoftwarePlan<ConsumerTier>[];
  }, [cfg]);
  const open = !!(cfg?.enabled && plans.length === TIERS.length);
  const chosen = plans.find((p) => p.tier === tier) || null;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const email = form.email.trim();
    if (form.full_name.trim().length < 2) return setErr("Please enter your full name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setErr("Please enter a valid email address.");
    if (form.phone.replace(/\D/g, "").length < 10) return setErr("Please enter a valid phone number.");
    if (open && !ack) return setErr("Please tick the box to confirm you understand this is do-it-yourself software.");
    setBusy(true);
    try {
      // 1) Save the lead first — only a confirmed save counts.
      const lead = await softwareSales.captureLead({
        full_name: form.full_name.trim(), business_name: "", email, phone: form.phone.trim(),
        website: form.website, tier, offer: "consumer", source: "ai_credit_funnel",
        utm, referrer: boundedReferrer(document.referrer),
      });
      if (!lead.captured) { setErr("We couldn't save your details just now. Please try again in a moment."); return; }
      if (!open) { setJoined(true); return; }
      // 2) Then Stripe Checkout.
      const r = await softwareSales.startConsumerCheckout(email, tier);
      if (chosen) pixelEvent("InitiateCheckout", { value: chosen.amount_cents / 100, currency: chosen.currency.toUpperCase(), content_name: `Dispute Manager ${chosen.label} (consumer)` });
      window.location.assign(r.url);
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.error : null;
      if (code === "sales_not_open") setJoined(true);
      else setErr(friendlyError(code, e));
    } finally { setBusy(false); }
  }

  return (
    <div className="sw">
      <header className="sw-nav">
        <div className="sw-shell sw-nav-inner">
          <span className="sw-logo"><img src="/brand-logo.png" alt="" />NO EXCUSES <em>DISPUTE MANAGER</em></span>
          <nav className="sw-nav-links">
            <a className="sw-btn sw-btn-ghost" href="/login">Sign in</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="sw-hero sw-hero-tight">
          <div className="sw-shell sw-hero-split">
            <div>
              <h1 className="sw-h1">Dispute your credit like the pros. <span>You just hit send.</span></h1>
              <p className="sw-lede">
                Your AI credit assistant: connect your report, and the software finds what you can dispute and writes
                your letters in minutes. You review, sign and send — and track every round in one place.
              </p>
              <ul className="sw-trust" aria-label="Why it's easy">
                <li><Timer size={16} aria-hidden="true" /> Letters in minutes</li>
                <li><BadgeCheck size={16} aria-hidden="true" /> You approve every dispute</li>
                <li><RotateCcw size={16} aria-hidden="true" /> Month to month</li>
                <li><CreditCard size={16} aria-hidden="true" /> Secure Stripe checkout</li>
              </ul>
              <a className="sw-btn sw-btn-primary sw-btn-xl" href="#order">Pick your plan <Zap size={17} aria-hidden="true" /></a>
            </div>
            <SalesVideo src={SALES_VIDEOS.consumer} title="See it work in 2 minutes" />
          </div>
        </section>

      <section className="sw-section" style={{ paddingTop: 8 }} id="order">
        <div className="sw-shell sw-buy">
          <div>
            <h2 className="sw-h2">Choose your plan</h2>
            {cfg === null ? (
              <div className="sw-soon"><Loader2 size={26} className="spin" aria-label="Loading plans" /></div>
            ) : !open ? (
              <div className="sw-price-card" style={{ marginTop: 14 }}>
                <div className="sw-soon">Opening soon</div>
                <p style={{ color: "var(--ne-muted)", margin: "6px 0 0", fontSize: 14 }}>
                  Leave your details and we'll reach out as soon as sign-ups open.
                </p>
              </div>
            ) : (
              <div className="sw-plans" role="radiogroup" aria-label="Choose a plan">
                {plans.map((p) => {
                  const copy = TIER_COPY[p.tier];
                  const active = p.tier === tier;
                  return (
                    <button type="button" key={p.tier} role="radio" aria-checked={active}
                      className={`sw-plan${active ? " is-active" : ""}`} onClick={() => setTier(p.tier)}>
                      {p.amount_cents < REGULAR_PRICE_CENTS && (
                        <span className="sw-plan-badge sw-plan-save">Save {money(REGULAR_PRICE_CENTS - p.amount_cents, p.currency)}/mo</span>
                      )}
                      <span className="sw-plan-name">{p.label}</span>
                      <span className="sw-plan-who">{copy.who}</span>
                      <span className="sw-price">
                        {p.amount_cents < REGULAR_PRICE_CENTS && (
                          <s className="sw-was" aria-label={`Regular price ${money(REGULAR_PRICE_CENTS, p.currency)}`}>{money(REGULAR_PRICE_CENTS, p.currency)}</s>
                        )}
                        {money(p.amount_cents, p.currency)}<small>/{p.interval}</small>
                      </span>
                      <span className="sw-plan-clients"><Users size={14} aria-hidden="true" /> {copy.people}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="sw-price-card" style={{ marginTop: 14 }}>
              <strong style={{ fontSize: 15 }}>How it works</strong>
              <ol className="sw-howto">
                <li>Connect your credit report — the software finds the items you can dispute and writes your letters.</li>
                <li>You review, sign and send every letter yourself.</li>
                <li>Track every round in one place. Attorney Brian, the AI paralegal, works on your own Claude account.</li>
              </ol>
              <p className="sw-fine" style={{ marginTop: 10 }}>
                Self-service software, not a credit repair service. You run your own disputes; we don't do them for you.
                Results vary, and no score increase or deletion is guaranteed.
              </p>
            </div>
            {/* Done-for-you is a separate service: a free call first, never a checkout here. */}
            <div className="sw-price-card sw-dfy" style={{ marginTop: 14 }} data-testid="done-for-you">
              <strong style={{ fontSize: 17 }}>Rather have us do it for you?</strong>
              <p style={{ color: "#c9ccd2", fontSize: 14.5, lineHeight: 1.6, margin: "8px 0 14px" }}>
                If you don't want to do it yourself, Mr. No Excuses and his team can handle your disputes for you.
                Start with a free call — we'll look at where you are and explain how it works.
                No cost for the call and no payment collected up front.
              </p>
              <a className="sw-btn sw-btn-ghost" href={DFY_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <CalendarClock size={16} aria-hidden="true" /> Book a free call
              </a>
              <p className="sw-fine" style={{ marginTop: 10 }}>Results vary. No one can promise a specific score or that any item will be removed.</p>
            </div>
          </div>

          <form className="sw-form" id="order-form" onSubmit={submit} noValidate>
            {joined ? (
              <div className="sw-ok" role="status">
                <strong>You're on the list.</strong><br />
                Thanks, {form.full_name.trim().split(/\s+/)[0] || "there"} — we'll contact you at {form.email.trim()} as soon as sign-ups open.
              </div>
            ) : (
              <>
                <h3>{open && chosen ? `Start ${chosen.label} — ${money(chosen.amount_cents, chosen.currency)}/${chosen.interval}` : "Get early access"}</h3>
                <p className="sw-note">
                  {open ? "Tell us who you are, then you'll finish securely on Stripe." : "Tell us who you are and we'll reach out as soon as it opens."}
                </p>
                {cancelled && !err && <p className="sw-error">Checkout was cancelled — nothing was charged. You can try again below.</p>}
                {err && <p className="sw-error" role="alert">{err}</p>}
                <div className="sw-field"><label htmlFor="sc-name">Full name</label>
                  <input id="sc-name" autoComplete="name" value={form.full_name} onChange={set("full_name")} maxLength={120} required /></div>
                <div className="sw-row2">
                  <div className="sw-field"><label htmlFor="sc-email">Email</label>
                    <input id="sc-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} maxLength={254} required /></div>
                  <div className="sw-field"><label htmlFor="sc-phone">Phone</label>
                    <input id="sc-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} maxLength={25} required /></div>
                </div>
                {/* Honeypot: invisible to people, filled by bots. */}
                <div className="sw-hp" aria-hidden="true">
                  <label htmlFor="sc-website">Website</label>
                  <input id="sc-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                </div>
                {open && (
                  <label className="sw-ack">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                    <span>I understand this is do-it-yourself software: I run my own disputes, No Excuses does not dispute for me, and no score increase or deletion is guaranteed.</span>
                  </label>
                )}
                <button className="sw-btn sw-btn-primary" type="submit" disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
                  {busy ? <Loader2 size={18} className="spin" aria-hidden="true" /> : open ? <Lock size={16} aria-hidden="true" /> : <Zap size={16} aria-hidden="true" />}
                  {busy ? "One moment…" : open ? "Continue to secure checkout" : "Join the early-access list"}
                </button>
                <p className="sw-fine">
                  <ShieldCheck size={13} aria-hidden="true" style={{ verticalAlign: -2 }} /> We'll use your details only to contact you about the Dispute Manager.
                  Not legal advice. Month to month; cancel anytime yourself in the app under Settings → Subscription.
                </p>
              </>
            )}
          </form>
        </div>
      </section>
      </main>
      {open && chosen && (
        <StickyStart targetId="order-form" label={`${chosen.label} — ${money(chosen.amount_cents, chosen.currency)}/mo`}
          sub={`Regular ${money(REGULAR_PRICE_CENTS, chosen.currency)}/mo · cancel anytime`} />
      )}

      <footer className="sw-footer">
        <div className="sw-shell">© {new Date().getFullYear()} Low Key Global Financial LLC · No Excuses Dispute Manager</div>
      </footer>
    </div>
  );
}
